import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';

import { CheckIcon, ChevronDownIcon } from './Icons';
import styles from './Select.module.css';
import type { SelectProps } from './Select.types';

/** The list never grows past this many pixels; a longer one scrolls. */
const MAX_LIST = 440;
/** Clear space kept between the list and the edge of the screen. */
const EDGE = 8;
/** The pause after which typed letters start a new word for the type-ahead. */
const TYPE_PAUSE = 700;

interface Place {
  readonly left: number;
  readonly width: number;
  readonly top: number | null;
  readonly bottom: number | null;
  readonly maxHeight: number;
}

/**
 * A branded replacement for the system's drop-down: a button that opens a list of choices with a check on the chosen one.
 * The pattern is the "select-only combobox" of the ARIA practices: focus stays on the button, the list is a listbox whose
 * active option is named by `aria-activedescendant`. The list is drawn on the page itself (a portal), so a window with a
 * scrolling body never clips it, and it opens upward when there is no room below.
 *
 * Keys: Arrow Down and Up (open, then move), Home and End, Enter and Space (choose, or open), Escape (close, the window
 * behind stays), Tab (close), letters (jump to the next choice that starts with them).
 */
export function Select({ id, options, value, onChange, testId }: SelectProps): React.JSX.Element {
  const listId = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const typed = useRef({ text: '', at: 0 });
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<Place | null>(null);
  const selected = Math.max(0, options.findIndex((option) => option.value === value));
  const [active, setActive] = useState(selected);
  const current = options[selected];

  const close = useCallback((): void => {
    setOpen(false);
    setPlace(null);
  }, []);

  const show = (): void => {
    setActive(selected);
    setOpen(true);
  };

  // measure the button and the list, then put the list under the button, or over it when there is more room there
  useLayoutEffect(() => {
    if (!open || trigger.current === null || list.current === null) return;
    const box = trigger.current.getBoundingClientRect();
    const screen = window.visualViewport?.height ?? window.innerHeight;
    const wanted = Math.min(list.current.scrollHeight + 2, MAX_LIST);
    const below = screen - box.bottom - EDGE;
    const above = box.top - EDGE;
    const up = below < wanted && above > below;
    const room = Math.max(96, Math.min(wanted, up ? above - 4 : below - 4));
    setPlace({
      left: box.left,
      width: box.width,
      top: up ? null : box.bottom + 4,
      bottom: up ? screen - box.top + 4 : null,
      maxHeight: room,
    });
  }, [open]);

  // keep the active choice in view
  useEffect(() => {
    if (!open || place === null) return;
    list.current?.querySelector<HTMLElement>(`[data-index="${String(active)}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, place, active]);

  // a list drawn over a window that moves or changes size would be left behind: close it
  useEffect(() => {
    if (!open) return undefined;
    const onMove = (event: Event): void => {
      if (event.target instanceof Node && list.current?.contains(event.target)) return;
      close();
    };
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, close]);

  const choose = (index: number): void => {
    const option = options[index];
    close();
    if (option !== undefined && option.value !== value) onChange(option.value);
  };

  const jump = (letters: string): void => {
    const now = Date.now();
    const text = (now - typed.current.at > TYPE_PAUSE ? '' : typed.current.text) + letters.toLowerCase();
    typed.current = { text, at: now };
    const start = text.length === 1 ? active + 1 : active;
    for (let step = 0; step < options.length; step += 1) {
      const index = (start + step) % options.length;
      if (options[index]?.label.toLowerCase().startsWith(text)) {
        if (!open) setOpen(true);
        setActive(index);
        return;
      }
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    const last = options.length - 1;
    const move = (index: number): void => {
      event.preventDefault();
      if (!open) setOpen(true);
      setActive(Math.min(last, Math.max(0, index)));
    };
    switch (event.key) {
      case 'ArrowDown':
        if (open) move(active + 1);
        else {
          event.preventDefault();
          show();
        }
        break;
      case 'ArrowUp':
        if (open) move(active - 1);
        else {
          event.preventDefault();
          show();
        }
        break;
      case 'Home':
        move(0);
        break;
      case 'End':
        move(last);
        break;
      case 'PageDown':
        move(active + 8);
        break;
      case 'PageUp':
        move(active - 8);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (open) choose(active);
        else show();
        break;
      case 'Escape':
        if (open) {
          // closes the list only: the window behind it stays
          event.preventDefault();
          event.stopPropagation();
          close();
        }
        break;
      case 'Tab':
        close();
        break;
      default:
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) jump(event.key);
    }
  };

  const style: CSSProperties | undefined =
    place === null
      ? { visibility: 'hidden', left: 0, top: 0 }
      : // the list is placed from the button's measured box, which is the same in both directions: physical coordinates are right here
        { left: place.left, width: place.width, maxHeight: place.maxHeight, ...(place.top === null ? { bottom: place.bottom ?? 0 } : { top: place.top }) };

  return (
    <>
      <button
        ref={trigger}
        id={id}
        type="button"
        role="combobox"
        className={styles.trigger}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? `${listId}-${String(active)}` : undefined}
        data-testid={testId}
        onClick={() => {
          if (open) close();
          else show();
        }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          if (open) close();
        }}
      >
        <span className={styles.value} lang={current?.lang} dir={current?.dir}>
          {current?.label}
        </span>
        <ChevronDownIcon size={14} className={styles.chevron} />
      </button>
      {open
        ? createPortal(
            <>
              {/* catches the click outside, so that it closes the list and does not reach the window's own backdrop */}
              <div
                className={styles.scrim}
                onMouseDown={(event) => {
                  event.preventDefault();
                  close();
                }}
              />
              <ul
                ref={list}
                id={listId}
                role="listbox"
                tabIndex={-1}
                className={styles.list}
                style={style}
                aria-labelledby={id}
                data-testid={testId === undefined ? undefined : `${testId}-list`}
                onMouseDown={(event) => {
                  // the focus stays on the button
                  event.preventDefault();
                }}
              >
                {options.map((option, index) => (
                  <li
                    key={option.value}
                    id={`${listId}-${String(index)}`}
                    role="option"
                    className={styles.option}
                    aria-selected={index === selected}
                    data-active={index === active ? '' : undefined}
                    data-index={index}
                    onMouseMove={() => {
                      if (index !== active) setActive(index);
                    }}
                    onClick={() => {
                      choose(index);
                    }}
                  >
                    <span lang={option.lang} dir={option.dir}>
                      {option.label}
                    </span>
                    {index === selected ? <CheckIcon size={14} className={styles.check} /> : null}
                  </li>
                ))}
              </ul>
            </>,
            document.body,
          )
        : null}
    </>
  );
}
